import {useEffect, useMemo, useState} from "react";
import {getAdminSiteSettings, setApiDebugEnabled} from "@zaaaz/shared";
import {applySiteAccentColor} from "@/lib/applySiteAccent";
import {Link, useLocation} from "react-router-dom";
import {
  Archive,
  BarChart3,
  ChevronDown,
  ExternalLink,
  FileText,
  Home,
  List,
  LogOut,
  Menu,
  Moon,
  PanelLeftClose,
  PanelLeftOpen,
  Percent,
  RotateCcw,
  Settings,
  ShoppingCart,
  Sun,
  TicketPercent,
  Users,
  UserCog,
  Wallet,
  Workflow
} from "lucide-react";
import {adminChangePassword, adminHeartbeat} from "@zaaaz/shared";
import {useAdminAuth} from "@/contexts/AdminAuthContext";
import {useAdminSidebarBadges} from "@/hooks/useAdminSidebarBadges";
import {adminBreadcrumbs, adminPageTitle} from "@/lib/adminBreadcrumbs";
import {adminToast} from "@/lib/adminToast";
import {Button} from "@/components/ui/button";
import {Input} from "@/components/ui/input";
import {Switch} from "@/components/ui/switch";
import {Label} from "@/components/ui/label";
import {Avatar, AvatarFallback} from "@/components/ui/avatar";
import {Tooltip, TooltipContent, TooltipProvider, TooltipTrigger} from "@/components/ui/tooltip";
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator
} from "@/components/ui/breadcrumb";
import {Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle} from "@/components/ui/dialog";
import {DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger} from "@/components/ui/dropdown-menu";
import {Sheet, SheetContent, SheetHeader, SheetTitle} from "@/components/ui/sheet";
import {cn} from "@/lib/utils";
import {AdminToaster} from "./AdminToaster";

const BASE = "/vadmin";
const THEME_KEY = "cc-admin-theme";
const SIDEBAR_KEY = "cc-admin-sidebar";

type NavItem = {
  href: string;
  label: string;
  icon: React.ComponentType<{className?: string}>;
  roles: number[];
  badgeKey?: keyof import("@/hooks/useAdminSidebarBadges").AdminSidebarBadges;
};
type NavGroup = {label: string; items: NavItem[]};

const NAV_GROUPS: NavGroup[] = [
  {
    label: "Main",
    items: [
      {href: BASE, label: "Dashboard", icon: Home, roles: [0, 1, 2]},
      {href: `${BASE}/integrari`, label: "Integrations", icon: Workflow, roles: [0, 1]}
    ]
  },
  {
    label: "Store",
    items: [
      {href: `${BASE}/comenzi`, label: "Orders", icon: ShoppingCart, roles: [0, 1], badgeKey: "orders"},
      {href: `${BASE}/comenzi/returnare`, label: "Returns", icon: RotateCcw, roles: [0, 1], badgeKey: "returns"},
      {href: `${BASE}/produse`, label: "Products", icon: Archive, roles: [0, 1]},
      {href: `${BASE}/clienti`, label: "Clients", icon: Users, roles: [0, 1]},
      {href: `${BASE}/comenzi/vouchere`, label: "Vouchers", icon: TicketPercent, roles: [0, 1]}
    ]
  },
  {
    label: "Influencers",
    items: [
      {href: `${BASE}/clienti/influencer-rates`, label: "Rates", icon: Percent, roles: [0, 1]},
      {href: `${BASE}/clienti/influencer-payouts`, label: "Payouts", icon: Wallet, roles: [0, 1]}
    ]
  },
  {
    label: "Management",
    items: [
      {href: `${BASE}/utilizatori`, label: "Users", icon: UserCog, roles: [0]},
      {href: `${BASE}/setari`, label: "Site settings", icon: Settings, roles: [0, 1]},
      {href: `${BASE}/log`, label: "Activity log", icon: List, roles: [0]}
    ]
  },
  {
    label: "Content & Marketing",
    items: [
      {href: `${BASE}/continut`, label: "Site content", icon: FileText, roles: [0, 1]},
      {href: `${BASE}/seo`, label: "SEO", icon: BarChart3, roles: [0, 1, 2]}
    ]
  }
];

function userInitials(name?: string): string {
  if(!name) return "?";
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if(parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
  return name.slice(0, 2).toUpperCase();
}

function roleLabel(rol?: number): string {
  if(rol === 0) return "Super admin";
  if(rol === 1) return "Admin";
  if(rol === 2) return "Editor";
  return "User";
}

function NavLinks({
  pathname,
  userRol,
  collapsed,
  badges,
  onNavigate
}: {
  pathname: string;
  userRol?: number;
  collapsed?: boolean;
  badges: import("@/hooks/useAdminSidebarBadges").AdminSidebarBadges;
  onNavigate?: () => void;
}) {
  function isActive(href: string) {
    if(pathname === href) return true;
    if(href === BASE) return false;
    // Orders must not highlight on Returns / Vouchers under /comenzi/*
    if(href === `${BASE}/comenzi`) {
      return (
        pathname === href
        || (pathname.startsWith(`${href}/`)
          && !pathname.startsWith(`${href}/returnare`)
          && !pathname.startsWith(`${href}/vouchere`))
      );
    }
    // Clients must not highlight on influencer routes under /clienti/influencer-*
    if(href === `${BASE}/clienti`) {
      return (
        pathname === href
        || (pathname.startsWith(`${href}/`) && !pathname.startsWith(`${href}/influencer`))
      );
    }
    return pathname.startsWith(`${href}/`) || pathname.startsWith(href);
  }

  function renderItem(item: NavItem) {
    const Icon = item.icon;
    const active = isActive(item.href);
    const count = item.badgeKey ? badges[item.badgeKey] : undefined;
    const badgeText = count != null && count > 0 ? (count > 99 ? "99+" : String(count)) : null;
    const link = (
      <Link
        key={item.href}
        to={item.href}
        onClick={onNavigate}
        className={cn(
          "admin-sidebar__link group relative flex items-center transition-all duration-200",
          active ? "admin-sidebar__link--active" : "admin-sidebar__link--idle",
          collapsed && "admin-sidebar__link--collapsed"
        )}
        aria-current={active ? "page" : undefined}
      >
        <Icon className={cn("admin-sidebar__icon shrink-0", active && "admin-sidebar__icon--active")} />
        {!collapsed ? (
          <>
            <span className="min-w-0 flex-1 truncate">{item.label}</span>
            {badgeText ? <span className="admin-sidebar__badge">{badgeText}</span> : null}
          </>
        ) : (
          badgeText ? <span className="admin-sidebar__badge admin-sidebar__badge--compact">{badgeText}</span> : null
        )}
      </Link>
    );

    if(collapsed) {
      return (
        <Tooltip key={item.href} delayDuration={0}>
          <TooltipTrigger asChild>{link}</TooltipTrigger>
          <TooltipContent side="right" className="font-medium">
            {item.label}
            {badgeText ? ` (${badgeText})` : ""}
          </TooltipContent>
        </Tooltip>
      );
    }
    return link;
  }

  return (
    <nav className={cn("admin-sidebar__nav", collapsed && "admin-sidebar__nav--collapsed")}>
      {NAV_GROUPS.map((group) => {
        const items = group.items.filter((item) => userRol === undefined || item.roles.includes(userRol));
        if(!items.length) return null;
        return (
          <div key={group.label} className="admin-sidebar__group">
            {!collapsed ? <p className="admin-sidebar__section">{group.label}</p> : null}
            <div className="admin-sidebar__items">{items.map(renderItem)}</div>
          </div>
        );
      })}
    </nav>
  );
}

export function AdminShell({children}: {children: React.ReactNode}) {
  const {pathname} = useLocation();
  const {user, logout} = useAdminAuth();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [collapsed, setCollapsed] = useState(() => localStorage.getItem(SIDEBAR_KEY) === "1");
  const [dark, setDark] = useState(() => localStorage.getItem(THEME_KEY) === "dark");
  const [pwdOpen, setPwdOpen] = useState(false);
  const [pwd, setPwd] = useState("");
  const [pwdMsg, setPwdMsg] = useState("");
  const [pwdErr, setPwdErr] = useState("");
  const badges = useAdminSidebarBadges(user?.rol === 0 || user?.rol === 1);

  const crumbs = useMemo(() => adminBreadcrumbs(pathname), [pathname]);
  const pageTitle = useMemo(() => adminPageTitle(pathname), [pathname]);
  useEffect(() => { setSidebarOpen(false); }, [pathname]);
  useEffect(() => { localStorage.setItem(THEME_KEY, dark ? "dark" : "light"); }, [dark]);
  useEffect(() => { localStorage.setItem(SIDEBAR_KEY, collapsed ? "1" : "0"); }, [collapsed]);
  useEffect(() => {
    document.body.dataset.adminUi = "1";
    return () => { delete document.body.dataset.adminUi; };
  }, []);

  useEffect(() => {
    getAdminSiteSettings().then((s) => {
      applySiteAccentColor(s.accentColor, document.querySelector(".admin-theme") as HTMLElement || document.documentElement);
      setApiDebugEnabled(!!s.debugErrors);
    }).catch(() => {});
  }, []);

  useEffect(() => {
    if(!user?.authenticated) return;
    const tick = () => adminHeartbeat(window.location.href).catch(() => {});
    tick();
    const t = setInterval(tick, 300000);
    return () => clearInterval(t);
  }, [user?.authenticated]);

  async function onChangePwd(e: React.FormEvent) {
    e.preventDefault();
    setPwdMsg("");
    setPwdErr("");
    if(pwd.length < 6) { setPwdErr("Password must be at least 6 characters."); return; }
    try {
      await adminChangePassword(pwd);
      adminToast.success("Password changed.");
      setPwd("");
      setPwdOpen(false);
    } catch(err) {
      setPwdErr(err instanceof Error ? err.message : "Error");
    }
  }

  const sidebarInner = (
    <>
      <div className={cn("admin-sidebar__brand flex h-16 shrink-0 items-center border-b border-sidebar-border", collapsed ? "justify-start px-3" : "gap-3 px-5")}>
        <img
          src="/images/logo.png"
          alt="ZaaaZ"
          className={cn("admin-sidebar__logo shrink-0 object-contain object-left", collapsed ? "h-8 w-auto max-w-[2.75rem]" : "h-9 w-auto max-w-[9rem]")}
        />
      </div>
      <div className="admin-sidebar__scroll min-h-0 flex-1 overflow-y-auto overscroll-contain">
        <NavLinks pathname={pathname} userRol={user?.rol} collapsed={collapsed} badges={badges} />
      </div>
      <div className="admin-sidebar__footer shrink-0">
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button
              type="button"
              className={cn("admin-sidebar__user", collapsed && "admin-sidebar__user--collapsed")}
              aria-label="Account menu"
            >
              <Avatar className="h-9 w-9 shrink-0 ring-1 ring-white/10">
                <AvatarFallback className="bg-primary text-xs font-semibold text-primary-foreground">
                  {userInitials(user?.nume)}
                </AvatarFallback>
              </Avatar>
              {!collapsed ? (
                <>
                  <span className="min-w-0 flex-1 text-left">
                    <span className="block truncate text-sm font-medium text-sidebar-foreground">{user?.nume || "..."}</span>
                    <span className="block truncate text-[11px] text-sidebar-muted">{roleLabel(user?.rol)}</span>
                  </span>
                  <ChevronDown className="h-4 w-4 shrink-0 text-sidebar-muted" />
                </>
              ) : null}
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent side={collapsed ? "right" : "top"} align={collapsed ? "start" : "center"} className="w-56">
            <div className="px-2 py-2">
              <p className="text-sm font-medium">{user?.nume}</p>
              {user?.email ? <p className="text-xs text-muted-foreground">{user.email}</p> : null}
            </div>
            <DropdownMenuSeparator />
            <DropdownMenuItem onSelect={() => setPwdOpen(true)}>Change password</DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem onSelect={() => { logout().then(() => { window.location.href = `${BASE}/login`; }); }}>
              <LogOut className="mr-2 h-4 w-4" /> Log out
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
        <Button
          variant="ghost"
          size={collapsed ? "icon" : "sm"}
          className={cn(
            "admin-sidebar__collapse text-sidebar-muted hover:bg-white/[0.04] hover:text-sidebar-foreground",
            !collapsed && "w-full justify-start gap-2"
          )}
          onClick={() => setCollapsed((c) => !c)}
          aria-label={collapsed ? "Expand menu" : "Collapse menu"}
        >
          {collapsed ? <PanelLeftOpen className="h-4 w-4" /> : <PanelLeftClose className="h-4 w-4" />}
          {!collapsed ? "Collapse menu" : null}
        </Button>
      </div>
    </>
  );

  return (
    <TooltipProvider>
      <div className={cn("admin-theme min-h-screen bg-background font-[family-name:var(--admin-font)]", dark && "dark")}>
        <div className="flex min-h-screen">
          <aside
            className={cn(
              "admin-sidebar sticky top-0 hidden h-screen shrink-0 flex-col border-r border-sidebar-border bg-sidebar text-sidebar-foreground lg:flex",
              collapsed ? "w-[var(--admin-sidebar-collapsed)]" : "w-[var(--admin-sidebar-width)]"
            )}
          >
            {sidebarInner}
          </aside>

          <Sheet open={sidebarOpen} onOpenChange={setSidebarOpen}>
            <SheetContent side="left" className="w-[var(--admin-sidebar-width)] border-sidebar-border bg-sidebar p-0 text-sidebar-foreground">
              <SheetHeader className="sr-only">
                <SheetTitle>Admin navigation</SheetTitle>
              </SheetHeader>
              <div className="admin-sidebar flex h-full min-h-0 flex-col">
                <div className="admin-sidebar__brand flex h-16 shrink-0 items-center justify-start border-b border-sidebar-border px-5">
                  <img src="/images/logo.png" alt="ZaaaZ" className="admin-sidebar__logo h-9 w-auto max-w-[9rem] object-contain object-left" />
                </div>
                <div className="admin-sidebar__scroll min-h-0 flex-1 overflow-y-auto overscroll-contain">
                  <NavLinks pathname={pathname} userRol={user?.rol} badges={badges} onNavigate={() => setSidebarOpen(false)} />
                </div>
              </div>
            </SheetContent>
          </Sheet>

          <div className="flex min-w-0 flex-1 flex-col">
            <header className="admin-topbar sticky top-0 z-40 border-b bg-[hsl(var(--admin-topbar))]/95 backdrop-blur supports-[backdrop-filter]:bg-[hsl(var(--admin-topbar))]/85">
              <div className="flex h-16 items-center gap-3 px-4 lg:px-6">
                <Button variant="ghost" size="icon" className="lg:hidden" onClick={() => setSidebarOpen(true)} aria-label="Open menu">
                  <Menu className="h-5 w-5" />
                </Button>
                <div className="hidden min-w-0 flex-1 lg:block">
                  <Breadcrumb>
                    <BreadcrumbList>
                      {crumbs.map((c, i) => (
                        <span key={c.href} className="contents">
                          {i > 0 ? <BreadcrumbSeparator /> : null}
                          <BreadcrumbItem>
                            {i === crumbs.length - 1 ? (
                              <BreadcrumbPage>{c.label}</BreadcrumbPage>
                            ) : (
                              <BreadcrumbLink asChild><Link to={c.href}>{c.label}</Link></BreadcrumbLink>
                            )}
                          </BreadcrumbItem>
                        </span>
                      ))}
                    </BreadcrumbList>
                  </Breadcrumb>
                </div>
                <div className="min-w-0 flex-1 lg:hidden">
                  <p className="truncate text-base font-semibold text-foreground">{pageTitle}</p>
                </div>
                <div className="ml-auto flex items-center gap-2">
                  <Button variant="outline" size="sm" className="hidden gap-2 md:inline-flex" asChild>
                    <a href="/" target="_blank" rel="noreferrer">
                      <ExternalLink className="h-4 w-4" />
                      Store
                    </a>
                  </Button>
                  <div className="hidden items-center gap-2 rounded-lg border bg-background px-2 py-1 sm:flex">
                    {dark ? <Moon className="h-3.5 w-3.5 text-muted-foreground" /> : <Sun className="h-3.5 w-3.5 text-muted-foreground" />}
                    <Switch id="admin-theme" checked={dark} onCheckedChange={setDark} aria-label="Toggle theme" className="scale-90" />
                  </div>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="ghost" className="gap-2 rounded-full px-2 hover:bg-accent">
                        <Avatar className="h-8 w-8 ring-2 ring-border">
                          <AvatarFallback className="bg-primary text-xs font-semibold text-primary-foreground">{userInitials(user?.nume)}</AvatarFallback>
                        </Avatar>
                        <span className="hidden max-w-[120px] truncate text-sm font-medium sm:inline">{user?.nume || "..."}</span>
                        <ChevronDown className="h-4 w-4 opacity-50" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" className="w-56">
                      <div className="px-2 py-2">
                        <p className="text-sm font-medium">{user?.nume}</p>
                        {user?.email ? <p className="text-xs text-muted-foreground">{user.email}</p> : null}
                      </div>
                      <DropdownMenuSeparator />
                      <DropdownMenuItem onSelect={() => setPwdOpen(true)}>Change password</DropdownMenuItem>
                      <DropdownMenuItem className="sm:hidden" onSelect={() => setDark((d) => !d)}>
                        {dark ? "Light mode" : "Dark mode"}
                      </DropdownMenuItem>
                      <DropdownMenuSeparator />
                      <DropdownMenuItem onSelect={() => { logout().then(() => { window.location.href = `${BASE}/login`; }); }}>
                        <LogOut className="mr-2 h-4 w-4" /> Log out
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
              </div>
            </header>
            <main className="admin-main flex-1">
              <div className="admin-main__inner">{children}</div>
            </main>
          </div>
        </div>

        <Dialog open={pwdOpen} onOpenChange={setPwdOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Change password</DialogTitle>
            </DialogHeader>
            <form onSubmit={onChangePwd} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="new-pwd">New password</Label>
                <Input id="new-pwd" type="password" placeholder="Min. 6 characters" value={pwd} onChange={(e) => setPwd(e.target.value)} minLength={6} required autoComplete="new-password" />
              </div>
              {pwdErr ? <p className="text-sm text-destructive">{pwdErr}</p> : null}
              {pwdMsg ? <p className="text-sm text-emerald-600">{pwdMsg}</p> : null}
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setPwdOpen(false)}>Cancel</Button>
                <Button type="submit">Save</Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
        <AdminToaster />
      </div>
    </TooltipProvider>
  );
}
